import { addResponseStatusToSpan } from '@vs-shop/engine/utils/httpTracing';
import type { Schema } from 'effect';
import { Effect } from 'effect';

import { createBackendApiError } from '@vs-shop/engine/data/error/backendApi/_internal/createBackendApiError';
import { schemaBodyJsonClean } from '@vs-shop/schemas/transform/schemaBodyJsonClean';
import { BackendApi } from '../../../service/api/BackendApi';
import { ApiModule } from '@vs-shop/engine/service/logger/ApiModule';
import { logApiOperation } from '@vs-shop/engine/utils/logger/apiLogger';
import * as HttpClientResponse from 'effect/unstable/http/HttpClientResponse';

const ENDPOINT = '/api/v2/shop/homepage';
const HTTP_METHOD = 'get';

export type MakeGetHomepageDeps<HomepageShowSchema extends Schema.Top> = {
  readonly homepageShow: HomepageShowSchema;
};

export const makeGetHomepage = <HomepageShowSchema extends Schema.Top>(
  deps: MakeGetHomepageDeps<HomepageShowSchema>
) => {
  const GetHomepageError = createBackendApiError({
    domain: 'Homepage',
    endpoint: 'Get',
    path: ENDPOINT,
    identifierField: 'id',
  });

  const getHomepageFetch = Effect.fn(`http.${HTTP_METHOD} ${ENDPOINT}`, {
    kind: 'client',
    attributes: {
      'http.request.method': HTTP_METHOD,
    },
  })(function* () {
    const apiClient = yield* BackendApi;
    return yield* apiClient
      .path(ENDPOINT)
      .method(HTTP_METHOD)
      .accept('application/ld+json')
      .fetch({});
  });

  const getHomepage = () =>
    getHomepageFetch().pipe(
      Effect.map((response) => response.response),
      Effect.tap(addResponseStatusToSpan),
      Effect.withSpan('ValidateResponse'),
      Effect.flatMap(
        HttpClientResponse.matchStatus({
          '2xx': (response) => schemaBodyJsonClean(deps.homepageShow)(response),
          404: (response) =>
            Effect.fail(new GetHomepageError.NotFoundError({ id: 'homepage', response })),
          orElse: (response) =>
            Effect.fail(new GetHomepageError.ApiRequestError({ id: 'homepage', response })),
        })
      ),
      Effect.tap(() =>
        logApiOperation(ApiModule.HomepageGet, 'Debug', 'Successfully resolved homepage')
      ),
      Effect.withSpan('api.homepage.get', {
        kind: 'internal',
      })
    );

  return { GetHomepageError, getHomepage };
};
